import React, { useCallback, useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { Callout } from "../../components/Callout";
import { DecisionCardOption } from "../../components/DecisionCards";
import { QuestionCard } from "../../components/QuestionCard";
import { SubStepShell } from "../../components/SubStepShell";
import { tallyLedgerApi, type TallyChart } from "../../data/tallyLedgerApi";
import { useSubStepNav } from "../useSubStepNav";
import { TallyCurrentLedgers } from "../tally-ledgers/TallyCurrentLedgers";
import { TallyLedgerImportPanel } from "../tally-ledgers/TallyLedgerImportPanel";
import { TallyNoBillCreatePanel, TallyNoBillMappingPanel } from "../tally-ledgers/TallyNoBillPanels";

const NO_BILLS_ANSWER_KEY = "verifySetup.noBillsPolicy";
const PER_BILL_LIMIT_DEFAULT = "10000";

interface NoBillsPolicy {
  choice?: "no-bills-ledger" | "direct-to-expense" | "map-existing";
  perBillLimit?: string;
}

const NO_BILLS_OPTIONS: DecisionCardOption[] = [
  {
    value: "no-bills-ledger",
    title: 'Create a "No Bills" ledger',
    description:
      "We add a No Bill - Expense group under Indirect Expenses, with a ledger under it for each kind of spend that arrives without one.",
  },
  {
    value: "map-existing",
    title: "They already have ledgers for this",
    description: "Point at the expense ledgers they already use. We record which ones they are and change nothing in Tally.",
  },
  {
    value: "direct-to-expense",
    title: "Post directly to expense ledgers",
    description: "Spend without a bill goes to the same expense ledgers as everything else.",
  },
];

export function TallyLedgersStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "ledgers");
  const noBillsPolicy = useCoAnswer<NoBillsPolicy>(clientId, NO_BILLS_ANSWER_KEY);
  const [chart, setChart] = useState<TallyChart | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reloadChart = useCallback(() => {
    tallyLedgerApi
      .chart(clientId)
      .then(setChart)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not read the chart of accounts.");
      });
  }, [clientId]);

  useEffect(() => {
    reloadChart();
  }, [reloadChart]);

  function selectNoBillsChoice(value: string) {
    const choice = value as NoBillsPolicy["choice"];
    void persistAnswer<NoBillsPolicy>(clientId, NO_BILLS_ANSWER_KEY, {
      ...noBillsPolicy,
      choice,
      perBillLimit:
        choice !== "direct-to-expense" && (noBillsPolicy?.perBillLimit ?? "") === ""
          ? PER_BILL_LIMIT_DEFAULT
          : noBillsPolicy?.perBillLimit,
    });
  }

  const choice = noBillsPolicy?.choice;
  const showLimit = choice === "no-bills-ledger" || choice === "map-existing";
  const ready = choice !== undefined && (!showLimit || (noBillsPolicy?.perBillLimit ?? "").trim() !== "");
  const clientName = client?.name || "this client";
  const companyName = chart?.company_name || clientName;

  return (
    <SubStepShell
      title="Set Up Your Chart of Accounts"
      description={`Below are the current Chart of Accounts available in ${companyName}'s Tally company.`}
      thenWhat={ready ? "Continue → Configure rounding off." : "Answer how expenses without bills are handled to continue."}
      onContinue={() => void goNext()}
      continueDisabled={!ready}
    >
      {error && <Callout variant="warning">{error}</Callout>}

      <TallyCurrentLedgers chart={chart} clientName={clientName} />

      <Callout variant="info">
        Bank and credit card accounts are added on the{" "}
        <span className="font-medium text-foreground">Banks &amp; Credit Cards</span> step, later in this section —
        not on this sheet. Each one needs a currency, which Tally fixes when the account is created and will not
        change afterwards, so the Chart of Accounts sheet leaves them out. Overdraft is the same. Bank ledgers
        already in Tally stay on this chart and are not edited on the sheet.
      </Callout>

      <TallyLedgerImportPanel clientId={clientId} companyName={companyName} onChanged={reloadChart} />

      <section className="flex flex-col gap-3 border-t pt-4">
        <QuestionCard
          headline="How should we account for expenses that do not have bills?"
          whyLine="In Zournal, expenses should generally have a bill. However, we understand that there may be smaller expenses for which a bill is not available. Please let us know how you would like these expenses to be accounted for."
          options={NO_BILLS_OPTIONS}
          selected={noBillsPolicy?.choice}
          onSelect={selectNoBillsChoice}
        />

        {showLimit && (
          <div className="flex flex-col gap-4">
            {choice === "no-bills-ledger" ? (
              <TallyNoBillCreatePanel clientId={clientId} companyName={companyName} onChanged={reloadChart} />
            ) : (
              <TallyNoBillMappingPanel clientId={clientId} clientName={clientName} />
            )}

            <label className="flex flex-col gap-1 text-sm sm:max-w-xs">
              <span className="text-xs font-medium text-muted-foreground">Maximum amount per bill posted to No Bills</span>
              <input
                type="number"
                value={noBillsPolicy?.perBillLimit ?? ""}
                onChange={(event) =>
                  void persistAnswer<NoBillsPolicy>(clientId, NO_BILLS_ANSWER_KEY, {
                    ...noBillsPolicy,
                    perBillLimit: event.target.value,
                  })
                }
                placeholder={PER_BILL_LIMIT_DEFAULT}
                className="h-9 rounded-md border bg-background px-3 text-sm"
              />
              <span className="text-xs text-muted-foreground">
                Usually ₹10,000. Cash above that to one person in a day is not an allowed expense.
              </span>
            </label>
          </div>
        )}
      </section>
    </SubStepShell>
  );
}
